import { Check, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { productFrequencyOptions, type ProductFrequency } from "./product-frequency";
import BrandImage from "./BrandImage";

type BrandDetailSheetProps = {
  brand: string;
  imageUrl?: string;
  frequency?: ProductFrequency;
  onClose: () => void;
  onToggleFrequency: (frequency: ProductFrequency) => Promise<void> | void;
  onRemove: () => Promise<void> | void;
  labels?: {
    details?: string;
    frequency?: string;
    remove?: string;
    close?: string;
    enlarge?: string;
    closeImage?: string;
  };
  frequencyLabels?: Partial<Record<ProductFrequency, string>>;
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "B";
}

export default function BrandDetailSheet({ brand, imageUrl, frequency, onClose, onToggleFrequency, onRemove, labels, frequencyLabels }: BrandDetailSheetProps) {
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (imagePreviewOpen) setImagePreviewOpen(false);
      else onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [imagePreviewOpen, onClose]);

  return <div className="product-detail-sheet" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="product-detail-sheet__panel brand-detail-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="brand-detail-title">
      <span className="product-detail-sheet__handle" aria-hidden="true" />
      <button type="button" className="product-detail-sheet__close" onClick={onClose} aria-label={labels?.close ?? "Close brand details"}><X /></button>
      <div className="product-detail-sheet__hero">
        {imageUrl ? <button type="button" className="brand-detail-sheet__image-trigger" onClick={() => setImagePreviewOpen(true)} aria-label={labels?.enlarge ?? `Enlarge ${brand} image`}><BrandImage src={imageUrl} loading="lazy" decoding="async" /></button> : <span className="brand-detail-sheet__fallback" aria-hidden="true">{initials(brand)}</span>}
        <div><small>{labels?.details ?? "BRAND DETAILS"}</small><h2 id="brand-detail-title">{brand}</h2></div>
      </div>
      <div className="product-detail-sheet__frequency">
        <strong>{labels?.frequency ?? "How often do I buy this brand?"}</strong>
        <div>{productFrequencyOptions.map((option) => <button type="button" key={option} className={frequency === option ? "active" : ""} aria-pressed={frequency === option} onClick={() => { void onToggleFrequency(option); }}>{frequency === option && <Check />}{frequencyLabels?.[option] ?? option}</button>)}</div>
      </div>
      <button type="button" className="product-detail-sheet__remove" onClick={() => { void onRemove(); }}><Trash2 /> {labels?.remove ?? "Remove brand"}</button>
    </section>
    {imageUrl && imagePreviewOpen && <div className="brand-image-preview" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setImagePreviewOpen(false); }}>
      <section className="brand-image-preview__dialog" role="dialog" aria-modal="true" aria-labelledby="brand-image-preview-title">
        <button type="button" className="brand-image-preview__close" onClick={() => setImagePreviewOpen(false)} aria-label={labels?.closeImage ?? "Close enlarged image"}><X /></button>
        <h2 id="brand-image-preview-title">{brand}</h2>
        <BrandImage src={imageUrl} alt={`${brand} product`} wrapperClassName="brand-image-preview__product" />
      </section>
    </div>}
  </div>;
}
